import { functionsUrl, supabase } from './supabase.ts';
import type {
  AgentIntelligence,
  AgentKind,
  AgentProvider,
  Category,
  Repo,
  Task,
  TaskKind,
  QuestionAnswer,
  TaskComment,
  TaskFollowUp,
  TaskQuestion,
  TaskSchedule,
  Subscription,
} from '@shared/domain.ts';
import type { OrphanPr } from '@shared/import.ts';
import type { DeployPlan } from '@shared/deployPlan.ts';

/** What a person edits on a schedule — everything else is the scheduler's. */
export interface ScheduleInput {
  cron: string;
  title: string;
  prompt: string;
  category: string | null;
  kind: TaskKind | null;
  auto_merge: boolean;
  smart: boolean;
  /** The routine every firing goes to; null is the rotation. */
  agent_id: string | null;
  /** No callback, and the task stays off the board. Needs `agent_id`. */
  fire_and_forget: boolean;
}

/**
 * Calls to the `api` edge function.
 *
 * Nothing here reads. Reads go straight to Postgres under RLS and arrive over
 * Realtime, so there is no fetch-after-write anywhere in this app: you call
 * this, it writes, and the row change comes back through the subscription like
 * any other. That is also why none of these need to return the new state —
 * they do, but the UI mostly ignores it.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function call<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError(401, 'signed out — sign in again');

  const response = await fetch(`${functionsUrl}/api${path}`, {
    method: init.method ?? 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
    },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
  });

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('application/json')) {
    const body = await response.text();
    if (!response.ok) throw new ApiError(response.status, body.slice(0, 300) || response.statusText);
    return body as T;
  }

  const body = await response.json();
  if (!response.ok) {
    throw new ApiError(response.status, body?.error ?? response.statusText, body?.detail);
  }
  return body as T;
}

const get = <T>(path: string) => call<T>(path, { method: 'GET' });
const post = <T>(path: string, body?: unknown) => call<T>(path, { method: 'POST', body: body ?? {} });
const patch = <T>(path: string, body: unknown) => call<T>(path, { method: 'PATCH', body });
const del = <T>(path: string) => call<T>(path, { method: 'DELETE' });

export interface AppConfig {
  functions_url: string;
  app_url: string;
  github: {
    app_configured: boolean;
    app_slug: string | null;
    app_id: string | null;
    install_url: string | null;
    webhook_url: string;
    setup_url: string;
  };
}

export const api = {
  config: () => get<AppConfig>('/config'),

  /* ── tasks ────────────────────────────────────────────────────────────── */

  submit: (input: {
    // Passed through as the confirmed row's id, so it lands under the same
    // identity the optimistic placeholder was shown with — see Composer.
    id?: string;
    project_id: string;
    prompt: string;
    repo_id?: string | null;
    category?: string | null;
    agent_id?: string | null;
    auto_merge?: boolean;
    smart?: boolean;
    kind?: TaskKind | null;
    /** The requester's exact words — shown on the board, never sent to a routine. */
    quote?: string | null;
  }) => post<{ task: Task; dispatched: boolean; queued: boolean; error: string | null }>('/tasks', input),
  /** Reclassify. Kind is the one thing about a task's arrival a person may change. */
  setKind: (id: string, kind: TaskKind | null) => patch<{ task: Task }>(`/tasks/${id}`, { kind }),

  signOff: (id: string, note?: string) => post<{ task: Task }>(`/tasks/${id}/sign-off`, { note }),
  approve: (id: string, note?: string) =>
    post<{ task: Task; approval: 'approve' | 'auto_merge' }>(`/tasks/${id}/approve`, { note }),
  markMerged: (id: string) => post<{ task: Task }>(`/tasks/${id}/merged`),
  markDeployed: (id: string, deploy_url?: string) =>
    post<{ task: Task }>(`/tasks/${id}/deployed`, { deploy_url }),
  /** Send it back for another pass — this is what a routine watches for. */
  sendBack: (id: string, note?: string) => post<{ task: Task }>(`/tasks/${id}/reopen`, { note }),
  /** Give an adopted pull request nobody is on to a routine — see `POST /tasks/:id/hand-off`. */
  handOff: (id: string, input: { agent_id?: string | null; comment?: string | null } = {}) =>
    post<{ task: Task; dispatched: boolean; queued: boolean; error: string | null }>(`/tasks/${id}/hand-off`, {
      agent_id: input.agent_id ?? null,
      comment: input.comment?.trim() || null,
    }),
  redispatch: (id: string, agent_id?: string | null) =>
    post<{ task: Task; dispatched: boolean; queued: boolean; error: string | null }>(`/tasks/${id}/redispatch`, {
      agent_id,
    }),
  /** Stop one — the door for a worker that died holding the merge gate. */
  cancelDesignPass: (id: string) => post<{ task: Task }>(`/tasks/${id}/design-pass/cancel`),
  syncGithub: (id: string) =>
    post<{ task: Task; posted_comments: number }>(`/tasks/${id}/sync`),
  comment: (id: string, body: string, commentId?: string) =>
    post<{ comment: TaskComment }>(`/tasks/${id}/comments`, { id: commentId, body }),
  /** Answer every open question on the task, as one batch. */
  answerQuestions: (id: string, answers: QuestionAnswer[]) =>
    post<{ questions: TaskQuestion[] }>(`/tasks/${id}/questions/answer`, { answers }),

  /* ── follow-ups ───────────────────────────────────────────────────────── */

  /** Turn a suggested follow-up into a task — as suggested, or edited. */
  acceptFollowUp: (
    id: string,
    input: {
      title?: string;
      prompt?: string;
      auto_merge?: boolean;
      smart?: boolean;
      kind?: TaskKind | null;
      agent_id?: string | null;
    },
  ) =>
    post<{ follow_up: TaskFollowUp; task: Task; dispatched: boolean; queued: boolean; error: string | null }>(
      `/follow-ups/${id}/accept`,
      input,
    ),
  rejectFollowUp: (id: string) => post<{ follow_up: TaskFollowUp }>(`/follow-ups/${id}/reject`),
  /**
   * Moves it to the trash — recoverable with `restore`. `closePrs` also closes
   * its open pull requests on GitHub, which restoring does not undo.
   */
  remove: (id: string, options: { closePrs?: boolean } = {}) =>
    del<{
      task: Task;
      pr_closed: number[];
      pr_close_failed: { number: number; error: string }[];
    }>(`/tasks/${id}${options.closePrs ? '?close_prs=1' : ''}`),
  restore: (id: string) => post<{ task: Task }>(`/tasks/${id}/restore`),
  /** Parks it: out of the working queues until `unsnooze`. */
  snooze: (id: string) => post<{ task: Task }>(`/tasks/${id}/snooze`),
  unsnooze: (id: string) => post<{ task: Task }>(`/tasks/${id}/unsnooze`),
  brief: (id: string) => get<string>(`/tasks/${id}/brief`),

  /** A place to put a file dragged or pasted into a message, before it is sent. */
  upload: (input: { project_id: string; filename: string; content_type?: string }) =>
    post<{
      upload_url: string;
      method: string;
      headers: Record<string, string>;
      public_url: string;
      path: string;
    }>('/uploads', input),

  /* ── projects and people ──────────────────────────────────────────────── */

  createProject: (name: string, repo: string) =>
    post<{ project: { id: string } }>('/projects', { name, repo }),
  renameProject: (id: string, name: string) => patch<unknown>(`/projects/${id}`, { name }),
  invite: (projectId: string, email: string, role: 'admin' | 'member') =>
    post<{ joined: boolean; invited: boolean }>(`/projects/${projectId}/invites`, { email, role }),
  setRole: (projectId: string, userId: string, role: 'admin' | 'member') =>
    patch<unknown>(`/projects/${projectId}/members/${userId}`, { role }),
  removeMember: (projectId: string, userId: string) =>
    del<unknown>(`/projects/${projectId}/members/${userId}`),
  cancelInvite: (id: string) => del<unknown>(`/invites/${id}`),

  /* ── repositories ─────────────────────────────────────────────────────── */

  addRepo: (input: { project_id: string; full_name: string; default_branch?: string }) =>
    post<{ repo: Repo }>('/repos', input),
  updateRepo: (
    id: string,
    input: { default_branch?: string; installation_id?: number | null; protected_paths?: string[] },
  ) =>
    patch<{ repo: Repo }>(`/repos/${id}`, input),
  removeRepo: (id: string) => del<unknown>(`/repos/${id}`),

  /* ── routines ─────────────────────────────────────────────────────────── */

  addAgent: (input: {
    project_id: string;
    name: string;
    url?: string | null;
    kind?: AgentKind;
    repo_id?: string | null;
    token?: string | null;
    provider?: AgentProvider;
    notes?: string | null;
    intelligence?: AgentIntelligence;
    subscription_id?: string | null;
  }) => post<{ agent: unknown }>('/agents', input),
  updateAgent: (
    id: string,
    input: {
      name?: string;
      url?: string | null;
      kind?: AgentKind;
      repo_id?: string | null;
      token?: string | null;
      provider?: AgentProvider;
      notes?: string | null;
      paused?: boolean;
      intelligence?: AgentIntelligence;
      subscription_id?: string | null;
    },
  ) => patch<{ agent: unknown }>(`/agents/${id}`, input),
  removeAgent: (id: string) => del<unknown>(`/agents/${id}`),
  /** Puts every routine in the project back to dispatch_seq 0 — the rotation starts over. */
  resetRotation: (projectId: string) => post<{ ok: true }>(`/projects/${projectId}/agents/reset-rotation`),
  /* ── subscriptions ────────────────────────────────────────────────────── */

  /** `token` is a `claude setup-token` key; it is checked as soon as it is saved. */
  addSubscription: (input: {
    project_id: string;
    name: string;
    token?: string | null;
    shared_project_ids?: string[];
  }) =>
    post<{ subscription: Subscription }>('/subscriptions', input),
  /** `token: null` removes the key; leaving it out keeps it. */
  updateSubscription: (
    id: string,
    input: { name?: string; token?: string | null; shared_project_ids?: string[] },
  ) =>
    patch<{ subscription: Subscription }>(`/subscriptions/${id}`, input),
  removeSubscription: (id: string) => del<unknown>(`/subscriptions/${id}`),
  checkSubscription: (id: string) => post<{ subscription: Subscription }>(`/subscriptions/${id}/check`),

  /** The dispatch queue job's last run, or null when it is not scheduled. */
  dispatchHealth: (projectId: string) =>
    get<{ queue_job: unknown | null }>(`/projects/${projectId}/dispatch-health`),

  agentTokens: (id: string) =>
    get<{
      tokens: {
        id: string;
        hint: string;
        label: string | null;
        created_at: string;
        last_used_at: string | null;
        revoked_at: string | null;
      }[];
    }>(`/agents/${id}/tokens`),
  /** The plaintext token is in this response and nowhere else, ever again. */
  mintAgentToken: (id: string, label?: string) =>
    post<{ token: string; instructions: string; record: { id: string; hint: string } }>(
      `/agents/${id}/tokens`,
      { label },
    ),
  revokeToken: (id: string) => del<unknown>(`/tokens/${id}`),

  /* ── categories ───────────────────────────────────────────────────────── */

  addCategory: (project_id: string, label: string, color?: string) =>
    post<{ category: Category }>('/categories', { project_id, label, color }),
  updateCategory: (id: string, input: { label?: string; color?: string | null }) =>
    patch<{ category: Category }>(`/categories/${id}`, input),
  removeCategory: (id: string) => del<unknown>(`/categories/${id}`),

  /** Every workflow the project's repositories define, asked of GitHub. */
  workflows: (projectId: string) =>
    get<{ workflows: string[]; failed: string[] }>(`/projects/${projectId}/workflows`),

  /** What a production deploy would ship now, and the inputs its workflow takes. */
  deployPlan: (projectId: string, repoId: string | null) =>
    get<DeployPlan>(`/projects/${projectId}/deploy${repoId ? `?repo_id=${repoId}` : ''}`),
  /** Run the production workflow on the default branch. */
  deploy: (projectId: string, input: { repo_id: string; inputs: Record<string, string | boolean> }) =>
    post<{ runs_url: string }>(`/projects/${projectId}/deploy`, input),

  /** Open pull requests in this project's repositories that no task accounts for. */
  orphanPrs: (projectId: string) =>
    get<{ prs: OrphanPr[]; failed: string[] }>(`/projects/${projectId}/orphan-prs`),

  importPrs: (project_id: string, prs: { repo_id: string; number: number }[]) =>
    post<{
      imported: { task: string; ref: number; url: string; commented: boolean }[];
      skipped: string[];
    }>('/tasks/import', { project_id, prs }),

  /* ── intakes ──────────────────────────────────────────────────────────── */

  addIntake: (input: {
    project_id: string;
    name: string;
    category?: string | null;
    agent_id?: string | null;
    auto_merge?: boolean;
    fire_and_forget?: boolean;
    smart?: boolean;
    kind?: TaskKind | null;
  }) => post<{ intake: { id: string }; url: string }>('/intakes', input),
  updateIntake: (
    id: string,
    input: {
      name?: string;
      category?: string | null;
      kind?: TaskKind | null;
      agent_id?: string | null;
      auto_merge?: boolean;
      fire_and_forget?: boolean;
      smart?: boolean;
    },
  ) => patch<{ intake: unknown }>(`/intakes/${id}`, input),
  removeIntake: (id: string) => del<{ ok: true }>(`/intakes/${id}`),
  /** The URL and both sets of instructions. Readable as often as you like. */
  intakeUrl: (id: string) =>
    get<{ url: string; creation: string; registration: string }>(`/intakes/${id}/url`),

  /* ── settings ─────────────────────────────────────────────────────────── */

  setGithubToken: (projectId: string, token: string | null) =>
    post<{ has_github_pat: boolean }>(`/projects/${projectId}/github-token`, { token }),
  setProjectSettings: (
    projectId: string,
    input: {
      agent_instructions?: string | null;
      design_instructions?: string | null;
      preview_workflow?: string | null;
      prod_workflow?: string | null;
      merge_waiver_check?: string | null;
      prod_version_url?: string | null;
      deploy_gate_check?: string | null;
      deploy_gate_bypass_input?: string | null;
    },
  ) => post<{ ok: true }>(`/projects/${projectId}/settings`, input),

  /* ── scheduled tasks ──────────────────────────────────────────────────── */

  schedules: (projectId: string) =>
    get<{ schedules: TaskSchedule[] }>(`/projects/${projectId}/schedules`),
  addSchedule: (input: ScheduleInput & { project_id: string }) =>
    post<{ schedule: TaskSchedule }>('/schedules', input),
  updateSchedule: (id: string, input: Partial<ScheduleInput>) =>
    patch<{ schedule: TaskSchedule }>(`/schedules/${id}`, input),
  removeSchedule: (id: string) => del<{ ok: true }>(`/schedules/${id}`),

  /* ── personal API keys ────────────────────────────────────────────────── */

  keys: () =>
    get<{
      keys: {
        id: string;
        hint: string;
        label: string | null;
        created_at: string;
        last_used_at: string | null;
        revoked_at: string | null;
      }[];
    }>('/keys'),
  /** The plaintext key is in this response and nowhere else, ever again. */
  mintKey: (label?: string) =>
    post<{ key: string; docs_url: string; record: { id: string; hint: string } }>('/keys', { label }),
  revokeKey: (id: string) => del<unknown>(`/keys/${id}`),
};
